import type { CatalogItem, PayoutRates, PermissionDef, PermissionGroup, RoleItem, StaffDetail } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { del, errorMessage, get, patch, post, put } from "../api";
import { Alert, Button, Checkbox, Dialog, Field, Loading, Panel, PanelHeader, TextAreaField, cx, formatDateTime } from "../components/ui";
import { PhotoInput } from "../components/PhotoInput";
import { SavedSignature } from "../components/SignatureInput";
import { StaffAlertsPanel } from "../components/StaffAlerts";
import { StaffCalendarPanel } from "../components/StaffCalendar";
import { useSession } from "../session";
import { useCatalog } from "./Catalog";
import { statusBadge } from "./Staff";

type OverrideValue = "role" | "grant" | "revoke";

export function StaffDetailPage() {
  const { id = "" } = useParams();
  const { me, can } = useSession();
  const qc = useQueryClient();
  const person = useQuery({ queryKey: ["staff", id], queryFn: () => get<StaffDetail>(`/api/staff/${id}`) });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get<RoleItem[]>("/api/roles") });
  const catalog = useQuery({
    queryKey: ["permissions"],
    queryFn: () => get<{ groups: PermissionGroup[]; permissions: PermissionDef[] }>("/api/permissions"),
  });

  const [profile, setProfile] = useState<{ firstName: string; lastName: string; phone: string; color: string | null; credentials: string }>({ firstName: "", lastName: "", phone: "", color: null, credentials: "" });
  const [website, setWebsite] = useState<{ show: boolean; title: string; bio: string; photoKey: string | null }>({ show: false, title: "", bio: "", photoKey: null });
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<Record<string, OverrideValue>>({});
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  // See the app as they do (read-only): the whole app reloads in their view.
  const viewAs = useMutation({ mutationFn: () => post<{ until: string }>(`/api/staff/${id}/view-as`), onSuccess: () => window.location.assign("/") });
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const p = person.data;
    if (!p) return;
    setProfile({ firstName: p.firstName, lastName: p.lastName, phone: p.phone ?? "", color: p.color, credentials: p.credentials });
    setWebsite({ show: p.website.show, title: p.website.title, bio: p.website.bio, photoKey: p.website.photoKey });
    setRoleIds(p.roleIds);
    setOverrides(Object.fromEntries(p.overrides.map((o) => [o.permission, o.effect])));
  }, [person.data]);

  const onSaved = async (d: StaffDetail, msg: string) => {
    qc.setQueryData(["staff", id], d);
    await qc.invalidateQueries({ queryKey: ["staff"] });
    await qc.invalidateQueries({ queryKey: ["roles"] });
    if (d.id === me?.staff.id) await qc.invalidateQueries({ queryKey: ["me"] });
    setNotice(msg);
  };

  const save = useMutation({
    mutationFn: () =>
      patch<StaffDetail>(`/api/staff/${id}`, {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone || null,
        color: profile.color,
        credentials: profile.credentials,
        showOnWebsite: website.show,
        websiteTitle: website.title,
        bio: website.bio,
        photoKey: website.photoKey,
        roleIds,
        ...(can("roles.manage")
          ? {
              overrides: Object.entries(overrides)
                .filter(([, v]) => v !== "role")
                .map(([permission, effect]) => ({ permission, effect })),
            }
          : {}),
      }),
    onSuccess: (d) => onSaved(d, "Changes saved."),
  });
  const deactivate = useMutation({
    mutationFn: () => post<StaffDetail>(`/api/staff/${id}/deactivate`),
    onSuccess: (d) => {
      setConfirmDeactivate(false);
      return onSaved(d, "Deactivated. They've been signed out everywhere.");
    },
  });
  const reactivate = useMutation({
    mutationFn: () => post<StaffDetail>(`/api/staff/${id}/reactivate`),
    onSuccess: (d) => onSaved(d, "Reactivated. They can sign in again."),
  });
  const resend = useMutation({
    mutationFn: () => post(`/api/staff/${id}/resend-invite`),
    onSuccess: async () => {
      setNotice("Invite sent. They'll get an email with a temporary password.");
      await qc.invalidateQueries({ queryKey: ["staff"] });
    },
  });
  const [confirmReset, setConfirmReset] = useState(false);
  const reset = useMutation({
    mutationFn: () => post(`/api/staff/${id}/reset-password`),
    onSuccess: () => {
      setConfirmReset(false);
      setNotice("Password reset. They've been emailed a temporary password and signed out everywhere; they'll choose a new one when they sign in.");
    },
  });

  const [confirmSignIn, setConfirmSignIn] = useState(false);
  const resetSignIn = useMutation({
    mutationFn: () => post(`/api/staff/${id}/reset-sign-in`),
    onSuccess: () => {
      setConfirmSignIn(false);
      setNotice("Sign-in reset. They're signed out everywhere; next time they sign in with their password and a code we email them, and their old passkeys are removed.");
    },
  });

  // Live preview of what this person will be able to do.
  const preview = useMemo(() => {
    if (!roles.data) return new Set<string>();
    const set = new Set<string>();
    for (const r of roles.data) if (roleIds.includes(r.id)) r.permissions.forEach((p) => set.add(p));
    for (const [p, v] of Object.entries(overrides)) {
      if (v === "grant") set.add(p);
      if (v === "revoke") set.delete(p);
    }
    return set;
  }, [roles.data, roleIds, overrides]);

  if (person.isLoading || roles.isLoading) return <Loading />;
  if (person.error) return <Alert>{errorMessage(person.error)}</Alert>;
  const p = person.data!;
  const isSelf = p.id === me?.staff.id;
  const error = save.error ?? deactivate.error ?? reactivate.error ?? resend.error ?? reset.error;

  const permissionsFromRoles = new Set<string>();
  for (const r of roles.data ?? []) if (roleIds.includes(r.id)) r.permissions.forEach((x) => permissionsFromRoles.add(x));

  return (
    <div>
      <Link to="/staff" className="text-sm font-medium text-muted hover:text-ink">
        Staff
      </Link>
      <header className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="page-title">
            {p.firstName} {p.lastName}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-ink-soft">
            {statusBadge(p)}
            <span>{p.email}</span>
            <span className="text-muted">Last sign-in {p.lastSignInAt ? formatDateTime(p.lastSignInAt, me?.clinic.timezone) : "never"}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("staff.view_as") && p.status !== "deactivated" && p.id !== me?.staff.id && (
            <Button onClick={() => viewAs.mutate()} busy={viewAs.isPending} title="See the app exactly as they do (read-only, 30 minutes)">
              View as {p.firstName}
            </Button>
          )}
          {p.status === "invited" && (
            <Button onClick={() => resend.mutate()} busy={resend.isPending}>
              {p.inviteSent ? "Resend invite" : "Send invite"}
            </Button>
          )}
          {p.status === "active" && (
            <>
              <Button onClick={() => setConfirmReset(true)}>Reset password</Button>
              <Button onClick={() => setConfirmSignIn(true)}>Reset sign-in</Button>
            </>
          )}
          {p.status === "deactivated" ? (
            <Button onClick={() => reactivate.mutate()} busy={reactivate.isPending}>
              Reactivate
            </Button>
          ) : (
            !isSelf && (
              <Button variant="danger" onClick={() => setConfirmDeactivate(true)}>
                Deactivate
              </Button>
            )
          )}
        </div>
      </header>
      {viewAs.error && <Alert>{errorMessage(viewAs.error)}</Alert>}

      {confirmReset && (
        <Dialog
          title={`Reset ${p.firstName}'s password?`}
          onClose={() => setConfirmReset(false)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setConfirmReset(false)}>
                Cancel
              </Button>
              <Button variant="primary" busy={reset.isPending} onClick={() => reset.mutate()}>
                Reset password
              </Button>
            </>
          }
        >
          <p>
            We'll email {p.email} a temporary password and sign them out everywhere. They choose a new password when they sign in; their sign-in codes and Face ID / fingerprint stay set up.
          </p>
        </Dialog>
      )}

      {confirmSignIn && (
        <Dialog
          title={`Reset ${p.firstName}'s sign-in?`}
          onClose={() => setConfirmSignIn(false)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setConfirmSignIn(false)}>
                Cancel
              </Button>
              <Button variant="primary" busy={resetSignIn.isPending} onClick={() => resetSignIn.mutate()}>
                Reset sign-in
              </Button>
            </>
          }
        >
          <p>For a lost or replaced phone. We'll sign {p.firstName} out everywhere, and:</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Their passkeys (Face ID / fingerprint) stop working right away, and are removed the next time they sign in.</li>
            <li>Sign-in codes come by email to {p.email} instead of an authenticator app.</li>
            <li>Their password stays the same (use Reset password if they've forgotten it too).</li>
          </ul>
          <p className="mt-2">Afterwards they can add a new passkey or authenticator under Your account.</p>
          {resetSignIn.error && <Alert>{errorMessage(resetSignIn.error)}</Alert>}
        </Dialog>
      )}

      <div className="mb-4 flex flex-col gap-2">
        {notice && <Alert tone="ok">{notice}</Alert>}
        {error && <Alert>{errorMessage(error)}</Alert>}
      </div>

      <form
        className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]"
        onSubmit={(e) => {
          e.preventDefault();
          setNotice(null);
          save.mutate();
        }}
      >
        <div className="flex flex-col gap-6">
          <Panel>
            <PanelHeader title="Profile" />
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
              <Field label="First name" required value={profile.firstName} onChange={(e) => setProfile({ ...profile, firstName: e.target.value })} />
              <Field label="Last name" required value={profile.lastName} onChange={(e) => setProfile({ ...profile, lastName: e.target.value })} />
              <Field label="Mobile" type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              <Field label="Email" value={p.email} disabled hint="Email is their sign-in and can't be changed here." />
              <Field label="Credentials" placeholder="RN BSN" maxLength={60} value={profile.credentials} onChange={(e) => setProfile({ ...profile, credentials: e.target.value })} hint="After their name on charts and the school and work notes they sign." />
              <label className="flex flex-col gap-1.5 text-sm font-semibold text-ink-soft">
                Colour on the Availability page
                <span className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Colour"
                    className="h-9 w-14 cursor-pointer rounded-[var(--radius-control)] border border-line bg-white p-1"
                    value={profile.color ?? "#2563EB"}
                    onChange={(e) => setProfile({ ...profile, color: e.target.value.toUpperCase() })}
                  />
                  {profile.color ? (
                    <Button variant="quiet" onClick={() => setProfile({ ...profile, color: null })}>
                      Automatic
                    </Button>
                  ) : (
                    <span className="text-xs font-normal text-muted">Automatic (by list order)</span>
                  )}
                </span>
              </label>
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="On the website" description="The clinic's website shows the people ticked here: name, credentials, title, bio, and photo (from the website feed)." />
            <div className="flex flex-col gap-4 px-5 py-5">
              <Checkbox label="Show on the website" checked={website.show} onChange={(on) => setWebsite({ ...website, show: on })} />
              <Field label="Title" placeholder="Registered Nurse · Co-founder" maxLength={80} value={website.title} onChange={(e) => setWebsite({ ...website, title: e.target.value })} />
              <TextAreaField label="Bio" rows={5} maxLength={2000} value={website.bio} onChange={(e) => setWebsite({ ...website, bio: e.target.value })} hint="A few sentences, written for clients." />
              <PhotoInput key={p.id} label="Headshot" url={p.website.photoUrl} onChange={(key) => setWebsite({ ...website, photoKey: key })} />
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Roles" description="A person can have more than one role. Their permissions combine." />
            <div className="grid gap-1 px-3 py-3 sm:grid-cols-2">
              {roles.data?.map((r) => (
                <Checkbox
                  key={r.id}
                  label={r.name}
                  description={r.description}
                  checked={roleIds.includes(r.id)}
                  onChange={(on) => setRoleIds((ids) => (on ? [...ids, r.id] : ids.filter((x) => x !== r.id)))}
                />
              ))}
            </div>
          </Panel>

          {can("roles.manage") && catalog.data && (
            <Panel>
              <PanelHeader
                title="Individual permissions"
                description="Use sparingly. These override this person's roles, one permission at a time."
              />
              <div className="overflow-x-auto">
                <table className="table-base min-w-[560px]">
                  <thead>
                    <tr>
                      <th>Permission</th>
                      <th>From roles</th>
                      <th>Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalog.data.permissions.map((perm) => (
                      <tr key={perm.key}>
                        <td>
                          <div className="font-medium">{perm.label}</div>
                          <div className="text-sm text-muted">{perm.available ? perm.description : "Used by a feature coming in a later step."}{perm.restricted && " Only a Super Admin can give this out."}</div>
                        </td>
                        <td className="text-sm">{permissionsFromRoles.has(perm.key) ? "Allowed" : <span className="text-muted">Not allowed</span>}</td>
                        <td>
                          <OverrideToggle
                            value={overrides[perm.key] ?? "role"}
                            onChange={(v) => setOverrides((o) => ({ ...o, [perm.key]: v }))}
                            label={perm.label}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          <div className="flex justify-end">
            <Button variant="primary" type="submit" busy={save.isPending}>
              Save changes
            </Button>
          </div>
        </div>

        <aside>
          <Panel className="sticky top-6">
            <PanelHeader title="What they can do" description="Updates as you change roles. Saved when you save." />
            <ul className="flex flex-col gap-2 px-5 py-4 text-sm">
              {catalog.data?.permissions.map((perm) => {
                const on = preview.has(perm.key);
                return (
                  <li key={perm.key} className={cx("flex items-start gap-2", !on && "text-muted")}>
                    <span className={cx("mt-1.5 size-2 shrink-0 rounded-full", on ? "bg-ok" : "border border-line")} aria-hidden />
                    <span>
                      {perm.label}
                      <span className="sr-only">{on ? " (allowed)" : " (not allowed)"}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </Panel>
        </aside>
      </form>

      <Panel className="mt-6">
        <PanelHeader title="Signature" description={`For school and work notes ${p.firstName} signs. They can also add it themselves under Your account.`} />
        <div className="px-5 py-5">
          <SavedSignature url={`/api/staff/${p.id}/signature`} signature={p.signature} onSaved={(signature) => qc.setQueryData(["staff", id], { ...p, signature })} />
        </div>
      </Panel>

      <StaffAlertsPanel
        className="mt-6"
        staffId={p.id}
        title="Alerts"
        description={`A text or email to ${p.firstName} when a visit is booked, cancelled, or moved: theirs, or all visits if they can see everyone's visits on Today. They can change these under Your account.`}
      />

      <StaffCalendarPanel className="mt-6" staffId={p.id} title="Calendar" description={`${p.firstName}'s visits on their phone calendar. They can also set this under Your account.`} />

      {can("payouts.manage") && <PayPanel staffId={p.id} name={p.firstName} />}

      {confirmDeactivate && (
        <Dialog
          title={`Deactivate ${p.firstName}?`}
          onClose={() => setConfirmDeactivate(false)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setConfirmDeactivate(false)}>
                Cancel
              </Button>
              <Button variant="danger" busy={deactivate.isPending} onClick={() => deactivate.mutate()}>
                Deactivate
              </Button>
            </>
          }
        >
          <p className="text-ink-soft">
            {p.firstName} will be signed out everywhere and won't be able to sign in. Their history stays in the audit log. You can reactivate them later.
          </p>
        </Dialog>
      )}
    </div>
  );
}

function OverrideToggle({ value, onChange, label }: { value: OverrideValue; onChange: (v: OverrideValue) => void; label: string }) {
  const options: { v: OverrideValue; text: string }[] = [
    { v: "role", text: "Use roles" },
    { v: "grant", text: "Allow" },
    { v: "revoke", text: "Deny" },
  ];
  return (
    <div role="radiogroup" aria-label={`Override for ${label}`} className="inline-flex rounded-[var(--radius-control)] border border-line bg-mist p-0.5">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          onClick={() => onChange(o.v)}
          className={cx(
            "rounded-[6px] px-2.5 py-1 text-xs font-semibold whitespace-nowrap",
            value === o.v
              ? o.v === "grant"
                ? "bg-ok text-white"
                : o.v === "revoke"
                  ? "bg-danger text-white"
                  : "bg-white text-ink shadow-sm"
              : "text-muted hover:text-ink",
          )}
        >
          {o.text}
        </button>
      ))}
    </div>
  );
}

/** This person's pay: their commission (blank = the clinic default) and any rates for single services. */
function PayPanel({ staffId, name }: { staffId: string; name: string }) {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["payout-settings"], queryFn: () => get<PayoutRates>("/api/payouts/settings") });
  const catalog = useCatalog();
  const items = (catalog.data?.items ?? []).filter((i: CatalogItem) => (i.type === "service" || i.type === "add_on") && i.status === "active");
  const mine = settings.data?.rates.filter((r) => r.staffMemberId === staffId) ?? [];
  const base = mine.find((r) => !r.catalogItemId) ?? null;
  const [percent, setPercent] = useState<string | null>(null);
  const [svc, setSvc] = useState({ catalogItemId: "", percent: "" });
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = async (msg?: string) => {
    await qc.invalidateQueries({ queryKey: ["payout-settings"] });
    await qc.invalidateQueries({ queryKey: ["payouts"] });
    setNotice(msg ?? null);
  };
  const saveBase = useMutation({
    mutationFn: async () => {
      const v = (percent ?? "").trim();
      if (v === "") return base ? del(`/api/payouts/rates/${base.id}`) : null;
      return post("/api/payouts/rates", { staffMemberId: staffId, catalogItemId: null, percent: Number(v) });
    },
    onSuccess: () => (setPercent(null), refresh("Commission saved.")),
  });
  const addSvc = useMutation({
    mutationFn: () => post("/api/payouts/rates", { staffMemberId: staffId, catalogItemId: svc.catalogItemId, percent: Number(svc.percent) }),
    onSuccess: () => (setSvc({ catalogItemId: "", percent: "" }), refresh()),
  });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/payouts/rates/${id}`), onSuccess: () => refresh() });
  const [overhead, setOverhead] = useState<string | null>(null);
  const [minOverhead, setMinOverhead] = useState<string | null>(null);
  const saveOverhead = useMutation({
    mutationFn: () =>
      put("/api/payouts/overhead", { staffMemberId: staffId, ...(overhead !== null ? { percent: Number(overhead) } : {}), ...(minOverhead !== null ? { minPercent: Number(minOverhead) } : {}) }),
    onSuccess: () => (setOverhead(null), setMinOverhead(null), refresh("Overhead saved.")),
  });
  if (!settings.data) return null;
  const value = percent ?? (base ? String(base.percent) : "");
  const overheadValue = overhead ?? String(settings.data.overhead[staffId] ?? 100);
  const minValue = minOverhead ?? String(settings.data.minOverhead[staffId] ?? 0);
  const bad = (v: string | null) => v !== null && (v === "" || Number(v) < 0 || Number(v) > 100);
  return (
    <Panel className="mt-6">
      <PanelHeader title="Pay" description={`Commission on what clients paid for ${name}'s services, add-ons, and travel. Tips are always 100% theirs.`} />
      <div className="flex flex-col gap-4 px-5 py-5">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            saveBase.mutate();
          }}
        >
          <Field label="Commission (%)" type="number" min={0} max={100} step="any" className="w-56" placeholder={`${settings.data.defaultPercent} (clinic default)`} value={value} onChange={(e) => setPercent(e.target.value)} hint="Blank uses the clinic default." />
          <Button type="submit" busy={saveBase.isPending} disabled={percent === null}>
            Save
          </Button>
          {notice && <span className="text-sm text-ok">{notice}</span>}
        </form>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            saveOverhead.mutate();
          }}
        >
          <Field
            label="Overhead taken out first (%)"
            type="number"
            min={0}
            max={100}
            step="any"
            className="w-56"
            value={overheadValue}
            onChange={(e) => setOverhead(e.target.value)}
            hint="Of what the revenue shares (medical director, ISP) take from a visit, before the commission is figured. 100 = all of it, 50 = split with them, 0 = none."
          />
          <Field
            label="Minimum overhead (% of what the client paid)"
            type="number"
            min={0}
            max={100}
            step="any"
            className="w-56"
            value={minValue}
            onChange={(e) => setMinOverhead(e.target.value)}
            hint="At least this much comes out of every visit, even one with no revenue share. 0 = no minimum."
          />
          <Button type="submit" busy={saveOverhead.isPending} disabled={(overhead === null && minOverhead === null) || bad(overhead) || bad(minOverhead)}>
            Save
          </Button>
        </form>
        <div>
          <div className="mb-1.5 text-sm font-semibold text-ink-soft">Different rate for a service</div>
          {mine.filter((r) => r.catalogItemId).length === 0 && <p className="text-sm text-muted">None.</p>}
          <ul className="flex flex-col gap-1">
            {mine
              .filter((r) => r.catalogItemId)
              .map((r) => (
                <li key={r.id} className="flex items-center gap-3 text-sm">
                  <span className="min-w-48">{catalog.data?.items.find((i: CatalogItem) => i.id === r.catalogItemId)?.name ?? "Removed item"}</span>
                  <span className="tabular-nums">{r.percent}%</span>
                  <Button variant="quiet" onClick={() => remove.mutate(r.id)}>
                    Remove
                  </Button>
                </li>
              ))}
          </ul>
          <form
            className="mt-2 flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              addSvc.mutate();
            }}
          >
            <label className="flex flex-col gap-1.5 text-sm font-semibold text-ink-soft">
              Service or add-on
              <select className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-2 text-[15px] font-normal text-ink" value={svc.catalogItemId} onChange={(e) => setSvc({ ...svc, catalogItemId: e.target.value })}>
                <option value="">Pick one</option>
                {items.map((i: CatalogItem) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </label>
            <Field label="Commission (%)" type="number" min={0} max={100} step="any" className="w-32" value={svc.percent} onChange={(e) => setSvc({ ...svc, percent: e.target.value })} />
            <Button type="submit" busy={addSvc.isPending} disabled={!svc.catalogItemId || svc.percent === ""}>
              Add
            </Button>
          </form>
        </div>
        {(saveBase.error || addSvc.error || remove.error || saveOverhead.error) && <Alert>{errorMessage(saveBase.error ?? addSvc.error ?? remove.error ?? saveOverhead.error)}</Alert>}
      </div>
    </Panel>
  );
}
